import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { Users, Server, DollarSign, ShoppingCart, RefreshCw, MessageSquare, ChevronRight, Loader, Radio } from 'lucide-react';
import IncidentManager from '../components/IncidentManager';

const PRIORITY_COLORS = {
  low: 'text-gray-400', normal: 'text-blue-400', high: 'text-yellow-400', urgent: 'text-red-400'
};
const PRIORITY_EMOJI = { low: '🟢', normal: '🔵', high: '🟡', urgent: '🔴' };

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${color}`}>
      <Icon className="w-6 h-6" />
    </div>
    <div className="text-3xl font-black text-white mb-1">{value}</div>
    <div className="text-gray-400 text-sm">{label}</div>
  </div>
);

// "2m ago" / "3h ago" / "5d ago" — with an online dot for very recent activity
const formatLastActive = (ts) => {
  if (!ts) return { label: 'Never', online: false, stale: true };
  const mins = Math.floor((Date.now() - new Date(ts).getTime()) / 60000);
  if (mins < 6)     return { label: 'Online now', online: true,  stale: false };
  if (mins < 60)    return { label: `${mins}m ago`, online: false, stale: false };
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)     return { label: `${hrs}h ago`,  online: false, stale: false };
  const days = Math.floor(hrs / 24);
  if (days < 30)    return { label: `${days}d ago`, online: false, stale: days > 7 };
  return { label: new Date(ts).toLocaleDateString(), online: false, stale: true };
};

const AdminPage = () => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [annForm, setAnnForm] = useState({ message: '', type: 'info' });
  const [couponForm, setCouponForm] = useState({ code: '', percent_off: 10, max_uses: '' });
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/admin/stats'),
      api.get('/admin/users'),
      api.get('/admin/orders'),
      api.get('/tickets/admin/all'),
      api.get('/reviews/admin/all'),
      api.get('/announcements'),
      api.get('/coupons'),
    ]).then(([s, u, o, t, r, a, c]) => {
      setStats(s.data.stats);
      setUsers(u.data.users);
      setOrders(o.data.orders);
      setTickets(t.data.tickets);
      setReviews(r.data.reviews);
      setAnnouncements(a.data.announcements);
      setCoupons(c.data.coupons);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const approveReview = async (id) => {
    try {
      await api.patch(`/reviews/${id}/approve`);
      setReviews((prev) => prev.map(r => r.id === id ? { ...r, status: 'approved' } : r));
    } catch { /* noop */ }
  };

  const deleteReview = async (id) => {
    if (!window.confirm('Delete this review permanently?')) return;
    try {
      await api.delete(`/reviews/${id}`);
      setReviews((prev) => prev.filter(r => r.id !== id));
    } catch { /* noop */ }
  };

  const postAnnouncement = async () => {
    if (annForm.message.trim().length < 3) return;
    try {
      const { data } = await api.post('/announcements', annForm);
      setAnnouncements((prev) => [data.announcement, ...prev.map(a => ({ ...a, active: false }))]);
      setAnnForm({ message: '', type: 'info' });
    } catch { /* noop */ }
  };

  const toggleAnnouncement = async (id) => {
    try {
      await api.patch(`/announcements/${id}/toggle`);
      setAnnouncements((prev) => prev.map(a =>
        a.id === id ? { ...a, active: !a.active } : { ...a, active: false }
      ));
    } catch { /* noop */ }
  };

  const deleteAnnouncement = async (id) => {
    try {
      await api.delete(`/announcements/${id}`);
      setAnnouncements((prev) => prev.filter(a => a.id !== id));
    } catch { /* noop */ }
  };

  const createCoupon = async () => {
    try {
      const { data } = await api.post('/coupons', {
        code: couponForm.code,
        percent_off: couponForm.percent_off,
        max_uses: couponForm.max_uses || null,
      });
      setCoupons((prev) => [data.coupon, ...prev]);
      setCouponForm({ code: '', percent_off: 10, max_uses: '' });
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to create coupon');
    }
  };

  const toggleCoupon = async (code) => {
    try {
      await api.patch(`/coupons/${code}/toggle`);
      setCoupons((prev) => prev.map(c => c.code === code ? { ...c, active: !c.active } : c));
    } catch { /* noop */ }
  };

  const deleteCoupon = async (code) => {
    if (!window.confirm(`Delete coupon ${code}?`)) return;
    try {
      await api.delete(`/coupons/${code}`);
      setCoupons((prev) => prev.filter(c => c.code !== code));
    } catch { /* noop */ }
  };

  const TABS = ['overview', 'users', 'orders', 'tickets', 'reviews', 'tools', 'status'];

  return (
    <div className="min-h-screen bg-gray-950 pt-20 px-4 pb-12">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-black text-white">Admin Panel</h1>
            <p className="text-gray-400 mt-1">PlarixMC Control Center</p>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-300 px-4 py-2 rounded-xl text-sm transition-colors"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 bg-gray-900 border border-gray-800 rounded-xl p-1 w-fit">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-colors ${
                tab === t
                  ? 'bg-green-500 text-gray-950'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-20 text-gray-400">Loading admin data...</div>
        ) : tab === 'overview' ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <StatCard icon={Users} label="Total customers" value={stats?.total_users ?? 0} color="bg-blue-500/10 text-blue-400" />
              <StatCard icon={Server} label="Active servers" value={stats?.total_servers ?? 0} color="bg-green-500/10 text-green-400" />
              <StatCard icon={DollarSign} label="Total revenue" value={`€${parseFloat(stats?.total_revenue ?? 0).toFixed(2)}`} color="bg-yellow-500/10 text-yellow-400" />
              <StatCard icon={ShoppingCart} label="Active orders" value={stats?.active_orders ?? 0} color="bg-purple-500/10 text-purple-400" />
              {stats?.suspended_orders > 0 && (
                <StatCard icon={MessageSquare} label="Suspended" value={stats.suspended_orders} color="bg-red-500/10 text-red-400" />
              )}
              {stats?.total_balance_held > 0 && (
                <StatCard icon={DollarSign} label="Balance held" value={`€${parseFloat(stats.total_balance_held).toFixed(2)}`} color="bg-green-500/10 text-green-400" />
              )}
            </div>

            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
              <h2 className="font-bold text-white mb-4">Recent Orders</h2>
              {orders.slice(0, 10).length === 0 ? (
                <p className="text-gray-500">No orders yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-gray-400 text-left border-b border-gray-800">
                        <th className="pb-3 font-medium">Customer</th>
                        <th className="pb-3 font-medium">Plan</th>
                        <th className="pb-3 font-medium">Status</th>
                        <th className="pb-3 font-medium">Amount</th>
                        <th className="pb-3 font-medium">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800">
                      {orders.slice(0, 10).map((o) => (
                        <tr key={o.id} className="text-gray-300">
                          <td className="py-3">{o.first_name} {o.last_name}<br /><span className="text-gray-500 text-xs">{o.email}</span></td>
                          <td className="py-3">{o.plan_name}</td>
                          <td className="py-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              o.status === 'active' ? 'bg-green-500/10 text-green-400' :
                              o.status === 'pending' ? 'bg-yellow-500/10 text-yellow-400' :
                              'bg-red-500/10 text-red-400'
                            }`}>{o.status}</span>
                          </td>
                          <td className="py-3">€{o.amount}</td>
                          <td className="py-3 text-gray-500">{new Date(o.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        ) : tab === 'users' ? (
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
            <h2 className="font-bold text-white mb-4">All Users ({users.length})</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-gray-400 text-left border-b border-gray-800">
                    <th className="pb-3 font-medium">UID</th>
                    <th className="pb-3 font-medium">Name</th>
                    <th className="pb-3 font-medium">Email</th>
                    <th className="pb-3 font-medium">Role</th>
                    <th className="pb-3 font-medium">Servers</th>
                    <th className="pb-3 font-medium">Last active</th>
                    <th className="pb-3 font-medium">Joined</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {users.map((u) => (
                    <tr key={u.id} className="text-gray-300">
                      <td className="py-3">
                        <div className="font-mono text-xs font-bold text-green-400">U-{u.customer_number}</div>
                        <div className="font-mono text-xs text-gray-600 mt-0.5 max-w-24 truncate" title={u.id}>{u.id}</div>
                      </td>
                      <td className="py-3">{u.first_name} {u.last_name}</td>
                      <td className="py-3 text-gray-400">{u.email}</td>
                      <td className="py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          u.role === 'admin' ? 'bg-purple-500/10 text-purple-400' : 'bg-gray-800 text-gray-400'
                        }`}>{u.role}</span>
                      </td>
                      <td className="py-3">{u.server_count}</td>
                      <td className="py-3">
                        {(() => {
                          const a = formatLastActive(u.last_active_at);
                          return (
                            <span className={`inline-flex items-center gap-1.5 ${a.online ? 'text-green-400' : a.stale ? 'text-gray-600' : 'text-gray-400'}`}>
                              {a.online && <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />}
                              {a.label}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="py-3 text-gray-500">{new Date(u.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : tab === 'tickets' ? (
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
            <h2 className="font-bold text-white mb-4 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-green-400" />
              Support Tickets ({tickets.length})
            </h2>
            {tickets.length === 0 ? (
              <p className="text-gray-500 text-sm">No tickets yet.</p>
            ) : (
              <div className="space-y-2">
                {tickets.map((ticket) => (
                  <Link
                    key={ticket.id}
                    to={`/tickets/${ticket.id}`}
                    className="flex items-center justify-between p-4 bg-gray-800/50 hover:bg-gray-800 rounded-xl transition-colors group"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-lg">{ticket.priority === 'urgent' ? '🔴' : ticket.priority === 'high' ? '🟡' : '🔵'}</span>
                      <div className="min-w-0">
                        <div className="font-medium text-white truncate">{ticket.subject}</div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {ticket.first_name} {ticket.last_name} · #{ticket.id.slice(0, 8).toUpperCase()} · {ticket.reply_count} messages · {new Date(ticket.updated_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        ticket.status === 'open'
                          ? 'bg-green-500/10 text-green-400'
                          : 'bg-gray-700 text-gray-400'
                      }`}>{ticket.status}</span>
                      <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-gray-400 transition-colors" />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        ) : tab === 'reviews' ? (
          <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-800 flex items-center justify-between">
              <h2 className="font-bold text-white">Customer Reviews</h2>
              <span className="text-gray-500 text-sm">
                {reviews.filter(r => r.status === 'pending').length} pending
              </span>
            </div>
            {reviews.length === 0 ? (
              <div className="text-center py-16 text-gray-500">No reviews yet</div>
            ) : (
              <div className="divide-y divide-gray-800">
                {reviews.map((review) => (
                  <div key={review.id} className="px-6 py-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1.5">
                          <span className="text-white font-semibold text-sm">{review.first_name} {review.last_name}</span>
                          <span className="text-gray-600 text-xs">{review.email}</span>
                          <span className="text-yellow-400 text-xs">{'★'.repeat(review.stars)}{'☆'.repeat(5 - review.stars)}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full border ${
                            review.status === 'approved'
                              ? 'bg-green-500/10 border-green-500/20 text-green-400'
                              : 'bg-yellow-500/10 border-yellow-500/20 text-yellow-400'
                          }`}>{review.status}</span>
                        </div>
                        <p className="text-gray-400 text-sm leading-relaxed">"{review.text}"</p>
                        <div className="text-gray-600 text-xs mt-1 font-mono">{review.tag}</div>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        {review.status === 'pending' && (
                          <button
                            onClick={() => approveReview(review.id)}
                            className="px-3 py-1.5 bg-green-500/10 hover:bg-green-500/20 border border-green-500/20 text-green-400 rounded-lg text-xs font-semibold transition-colors"
                          >
                            Approve
                          </button>
                        )}
                        <button
                          onClick={() => deleteReview(review.id)}
                          className="px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-400 rounded-lg text-xs transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : tab === 'tools' ? (
          <div className="space-y-6">
            {/* Announcements */}
            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
              <h2 className="font-bold text-white mb-1">Announcement banner</h2>
              <p className="text-gray-500 text-sm mb-4">Shown site-wide to all visitors. Only one can be active at a time.</p>
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={annForm.message}
                  onChange={(e) => setAnnForm({ ...annForm, message: e.target.value })}
                  placeholder="e.g. Scheduled maintenance tonight 22:00–23:00 CET"
                  className="flex-1 bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-green-500 transition-colors placeholder-gray-600"
                />
                <select
                  value={annForm.type}
                  onChange={(e) => setAnnForm({ ...annForm, type: e.target.value })}
                  className="bg-gray-800 border border-gray-700 rounded-xl px-3 text-white text-sm focus:outline-none"
                >
                  <option value="info">Info</option>
                  <option value="warning">Warning</option>
                  <option value="success">Success</option>
                </select>
                <button onClick={postAnnouncement} className="bg-green-500 hover:bg-green-400 text-gray-950 font-bold px-4 rounded-xl text-sm transition-colors">
                  Publish
                </button>
              </div>
              <div className="space-y-2">
                {announcements.map((a) => (
                  <div key={a.id} className="flex items-center justify-between bg-gray-800/60 rounded-xl px-4 py-2.5 text-sm">
                    <span className="text-gray-300 flex-1 truncate pr-3">{a.message}</span>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${a.active ? 'bg-green-500/10 border-green-500/20 text-green-400' : 'bg-gray-700 border-gray-600 text-gray-500'}`}>
                        {a.active ? 'active' : 'off'}
                      </span>
                      <button onClick={() => toggleAnnouncement(a.id)} className="text-gray-400 hover:text-white text-xs">Toggle</button>
                      <button onClick={() => deleteAnnouncement(a.id)} className="text-red-400 hover:text-red-300 text-xs">Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Coupons */}
            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
              <h2 className="font-bold text-white mb-1">Coupon codes</h2>
              <p className="text-gray-500 text-sm mb-4">Applied at checkout with Balance payments. Percentage off the first payment.</p>
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={couponForm.code}
                  onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })}
                  placeholder="LAUNCH20"
                  className="flex-1 bg-gray-800 border border-gray-700 rounded-xl px-4 py-2.5 text-white text-sm font-mono focus:outline-none focus:border-green-500 transition-colors placeholder-gray-600"
                />
                <input
                  type="number"
                  min="1" max="100"
                  value={couponForm.percent_off}
                  onChange={(e) => setCouponForm({ ...couponForm, percent_off: e.target.value })}
                  className="w-20 bg-gray-800 border border-gray-700 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none"
                  title="% off"
                />
                <input
                  type="number"
                  min="1"
                  value={couponForm.max_uses}
                  onChange={(e) => setCouponForm({ ...couponForm, max_uses: e.target.value })}
                  placeholder="Max uses"
                  className="w-28 bg-gray-800 border border-gray-700 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none placeholder-gray-600"
                />
                <button onClick={createCoupon} disabled={!couponForm.code} className="bg-green-500 hover:bg-green-400 disabled:opacity-40 text-gray-950 font-bold px-4 rounded-xl text-sm transition-colors">
                  Create
                </button>
              </div>
              <div className="space-y-2">
                {coupons.map((c) => (
                  <div key={c.code} className="flex items-center justify-between bg-gray-800/60 rounded-xl px-4 py-2.5 text-sm">
                    <div className="flex items-center gap-3">
                      <span className="text-white font-mono">{c.code}</span>
                      <span className="text-green-400">−{c.percent_off}%</span>
                      <span className="text-gray-500 text-xs">{c.uses}{c.max_uses ? `/${c.max_uses}` : ''} used</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${c.active ? 'bg-green-500/10 border-green-500/20 text-green-400' : 'bg-gray-700 border-gray-600 text-gray-500'}`}>
                        {c.active ? 'active' : 'off'}
                      </span>
                      <button onClick={() => toggleCoupon(c.code)} className="text-gray-400 hover:text-white text-xs">Toggle</button>
                      <button onClick={() => deleteCoupon(c.code)} className="text-red-400 hover:text-red-300 text-xs">Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : tab === 'status' ? (
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
            <IncidentManager />
          </div>
        ) : (
          <div className="text-center py-20 text-gray-500">Select a tab above</div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
